'use client';

import React, { RefCallback, useState } from 'react';

import { Box, BoxContent, Column, Columns, Divider, Title, TitleLevels } from '@bytel/trilogy-react';

import { ContractType } from '@app-types/contract';
import { FundingType } from '@app-types/funding';
import { MobilePlanType } from '@app-types/plan';
import { Product, ProductChildFull, ProductType } from '@app-types/product';
import { UserType } from '@app-types/user';

import { ProductDetailsModal } from '@components/product-details-modal';

import { getCurrentPhone } from '@helpers/cart';

import { TradeInStep } from '@constants/trade-in';

import { ProductCTA, SkeletonTicket } from '@product-detail/components';
import { PricesDetails, isOutOfStock } from '@product-detail/helpers';
import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

import { TicketFunding } from './ticket-funding';
import { TicketPlan } from './ticket-plan';
import { TicketProduct } from './ticket-product';
import { TicketRenewalCTA } from './ticket-renewal-cta';
import { TicketTradeIn } from './ticket-trade-in';

export type ProductTicketProps = {
    currentProduct: ProductChildFull;
    plan?: MobilePlanType;
    openPlanModal: () => void;
    tradeInStep: TradeInStep;
    tradeInBonus: number;
    openTradeInModal: () => void;
    openShopAvailabilityModal: ClickEvent;
    selectedFunding?: FundingType;
    isLoading: boolean;
    showRef: RefCallback<HTMLDivElement>;
    user?: UserType;
    contract?: ContractType;
    prices: PricesDetails;
    isRenewal: boolean;
    renewalRedirectWall?: boolean;
};

export function Ticket({
    currentProduct,
    plan,
    openPlanModal,
    tradeInStep,
    tradeInBonus,
    openTradeInModal,
    openShopAvailabilityModal,
    selectedFunding,
    isLoading,
    showRef,
    contract,
    user,
    prices,
    isRenewal,
    renewalRedirectWall,
}: Readonly<ProductTicketProps>) {
    const [isShowPriceDetailsModal, setIsShowPriceDetailsModal] = useState(false);

    const { productPrices } = prices;

    const phoneInCart: Product = getCurrentPhone(plan?.simulatedCart) || (currentProduct as Product);

    if (isLoading || productPrices.marketingAmount <= 0) {
        return (
            <SkeletonTicket
                showRef={showRef}
                isPhone={currentProduct.type === ProductType.PHONE}
                isRenewal={isRenewal}
            />
        );
    }

    return (
        <Box shadowless className='remove-section-padding'>
            <BoxContent>
                <Columns>
                    <Column>
                        <Title level={TitleLevels.FIVE}>{isRenewal ? 'Mon offre actuelle' : 'Mes achats'}</Title>
                    </Column>
                </Columns>
                <TicketProduct
                    currentProduct={currentProduct}
                    onClickDetail={() => setIsShowPriceDetailsModal(true)}
                    isRenewal={isRenewal}
                    prices={prices}
                />
                {currentProduct.type === ProductType.PHONE && tradeInStep === TradeInStep.CONFIRMED && (
                    <TicketTradeIn
                        tradeInStep={tradeInStep}
                        tradeInBonus={tradeInBonus}
                        openTradeInModal={openTradeInModal}
                    />
                )}
                {!isLoading && plan && <TicketPlan plan={plan} openPlanModal={openPlanModal} prices={prices} />}
                <TicketFunding prices={prices} />
                <Divider />
                <div ref={showRef}>
                    <Columns>
                        {isRenewal && !isOutOfStock(currentProduct.stockDetails.stock.status) && (
                            <TicketRenewalCTA
                                currentProduct={currentProduct}
                                contractId={contract?.id}
                                user={user}
                                renewalRedirectWall={renewalRedirectWall}
                            />
                        )}
                        <ProductCTA
                            currentProduct={currentProduct}
                            openShopAvailabilityModal={openShopAvailabilityModal}
                            plan={plan}
                            fullwidth
                            contract={contract}
                            user={user}
                            isRenewal={isRenewal}
                            renewalRedirectWall={renewalRedirectWall}
                        />
                    </Columns>
                </div>
                <ProductDetailsModal
                    plan={plan}
                    currentProduct={phoneInCart}
                    onClose={() => setIsShowPriceDetailsModal(false)}
                    type={currentProduct.type}
                    selectedFunding={selectedFunding}
                    modalOpen={isShowPriceDetailsModal}
                />
            </BoxContent>
        </Box>
    );
}
